import React, { ReactNode, useContext, useEffect, useState } from 'react';

import DateRangeContext from '@totem/components/common/dateRangePicker/dateRangeContext';
import BillingContext from '@totem/components/ticketing/billing/billingContext';
import {
  BilledEntry,
  BillingGetRequest,
  UnbilledTimeEntry,
} from '@totem/components/ticketing/ticketing';
import { BillingInput } from '@totem/components/ticketing/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull, isNull } from '@totem/utilities/common';
import { TICKETS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
type Props = {
  children?: ReactNode;
  billingParams?: BillingGetRequest;
  refreshCount?: number;
  billingId?: string;
};
const BillingContainer = ({
  children,
  billingParams,
  refreshCount,
  billingId,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { startDate, endDate } = useContext(DateRangeContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(true);
  const [refreshBilled, setRefreshBilled] = useState<boolean>(true);
  const [rawUnbilled, setRawUnbilled] = useState<UnbilledTimeEntry[]>([]);
  const [rawBilled, setRawBilled] = useState<BilledEntry[]>([]);
  const [unbilled, setUnbilled] = useState<UnbilledTimeEntry[]>([]);
  const [billed, setBilled] = useState<BilledEntry[]>([]);
  const [selectedUnbilled, setSelectedUnbilled] = useState<UnbilledTimeEntry[]>(
    [],
  );
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [action, setAction] = React.useState<string>('');
  const [actionExtras, setActionExtras] = React.useState<string>('');
  const [totalBilled, setTotalBilled] = useState<number>(0);
  const [totalUnbilled, setTotalUnbilled] = useState<number>(0);
  const [input, updateInput] = useState<BillingInput>({
    startDate: null,
    endDate: null,
  });
  const setInput = (updated: Partial<BillingInput>) => {
    updateInput({ ...input, ...updated });
  };
  useEffect(() => {
    setRefresh(true);

    setRefreshBilled(true);
  }, [refreshCount, userProfile.organization.id]);

  useEffect(() => {
    updateInput((prevState) => ({
      ...prevState,
      startDate,
      endDate,
    }));
  }, [startDate, endDate]);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      const payload = isNotNull(billingParams)
        ? billingParams
        : { resourceEmails: [], ticketIds: [] };
      fetch(`${TICKETS_ENDPOINT}/unbilled`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      })
        .then((res) => {
          CheckResponseShowError(res);
          return res;
        })
        .then((res) => res.json())
        .then((result: UnbilledTimeEntry[]) => {
          if (isNotNull(result)) {
            setRawUnbilled(result);
          }
        })
        .then(() => setLoading(false))
        .then(() => setRefreshBilled(true));
    }
  }, [refresh, getApiToken]);

  useEffect(() => {
    if (refreshBilled) {
      setRefreshBilled(false);
      const payload = isNotNull(billingParams)
        ? billingParams
        : { resourceEmails: [], ticketIds: [] };
      const endPoint = isNull(billingId)
        ? `${TICKETS_ENDPOINT}/billed`
        : `${TICKETS_ENDPOINT}/billed/${billingId}`;
      fetch(endPoint, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      })
        .then((res) => {
          CheckResponseShowError(res);
          return res;
        })
        .then((res) => res.json())
        .then((result: BilledEntry[]) => {
          if (isNotNull(result)) {
            setRawBilled(result);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [refreshBilled, getApiToken]);

  useEffect(() => {
    if (selectedUnbilled.length === 0 && selectedOrganization !== '') {
      setSelectedOrganization('');
    }
  }, [selectedUnbilled]);

  useEffect(() => {
    let filteredData = JSON.parse(JSON.stringify(rawBilled));
    if (isNotNull(input.startDate)) {
      for (let idx = 0; idx < filteredData.length; idx++) {
        filteredData[idx].items = filteredData[idx].items.filter(
          (item) => item.timeEntry.date >= input.startDate.toISOString(),
        );
      }
    }
    if (isNotNull(input.endDate)) {
      for (let idx = 0; idx < filteredData.length; idx++) {
        filteredData[idx].items = filteredData[idx].items.filter(
          (item) => item.timeEntry.date <= input.endDate.toISOString(),
        );
      }
    }
    // Remove records that no longer have items.
    filteredData = filteredData.filter((item) => item.items.length > 0);

    setBilled(filteredData);
  }, [rawBilled, input]);

  useEffect(() => {
    let filteredData = JSON.parse(JSON.stringify(rawUnbilled));
    if (isNotNull(input.startDate)) {
      filteredData = filteredData.filter(
        (item) => item.timeEntry.date >= input.startDate.toISOString(),
      );
    }
    if (isNotNull(input.endDate)) {
      filteredData = filteredData.filter(
        (item) => item.timeEntry.date <= input.endDate.toISOString(),
      );
    }
    setUnbilled(filteredData);
  }, [rawUnbilled, input]);

  useEffect(() => {
    let newUnbilled = 0;
    let newBilled = 0;
    if (isNotNull(unbilled)) {
      unbilled.forEach((item) => {
        if (item.timeEntry.billable && item.timeEntry.billingId === '') {
          newUnbilled += item.timeEntry.hoursLogged;
        }
      });
    }
    if (isNotNull(billed)) {
      billed.forEach((item) => {
        item.items.forEach((subItem) => {
          if (
            subItem.timeEntry.billable &&
            subItem.timeEntry.billingId !== ''
          ) {
            newBilled += subItem.timeEntry.hoursLogged;
          }
        });
      });
    }
    setTotalBilled(newBilled);

    setTotalUnbilled(newUnbilled);
  }, [unbilled, billed]);

  const selectAllUnbilled = () => {
    if (rawBilled.length > 0) {
      let organizationId = rawUnbilled[0].organization.id;
      if (selectedUnbilled.length === 0) {
        setSelectedOrganization(organizationId);
      } else {
        organizationId = selectedOrganization;
      }
      setSelectedUnbilled(
        rawUnbilled.filter((item) => item.organization.id === organizationId),
      );
    }
  };

  const selectUnbilled = (item: UnbilledTimeEntry) => {
    if (isNotNull(selectedUnbilled)) {
      const idx = selectedUnbilled.findIndex((chk) => chk.id === item.id);
      if (idx < 0) {
        setSelectedUnbilled([...selectedUnbilled, item]);

        setSelectedOrganization(item.organization.id);
      }
    } else {
      setSelectedUnbilled([item]);

      setSelectedOrganization(item.organization.id);
    }
  };

  const deselectUnbilled = (item: UnbilledTimeEntry) => {
    if (isNotNull(selectedUnbilled)) {
      setSelectedUnbilled(selectedUnbilled.filter((chk) => chk.id !== item.id));
    } else {
      setSelectedUnbilled([]);
    }
  };

  const clearSelectedUnbilled = () => {
    setSelectedUnbilled([]);
  };

  const handleAction = (newAction: string, newActionExtras: string) => {
    switch (newAction) {
      case 'Refresh':
      case 'refresh':
        setRefresh(true);

        setAction('');

        setActionExtras('');
        break;
      default:
        setAction(newAction);

        setActionExtras(newActionExtras);
        break;
    }
  };

  return (
    <BillingContext.Provider
      value={{
        loading,
        unbilledData: unbilled,
        billedData: billed,
        action,
        actionExtras,
        onAction: handleAction,
        selectedUnbilled,
        selectUnbilled,
        deselectUnbilled,
        selectAllUnbilled,
        clearSelectedUnbilled,
        selectedOrganizationId: selectedOrganization,
        totalBilled,
        totalUnbilled,
        input,
        setInput,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};
export default BillingContainer;
